import React, { useEffect, useState } from "react";
import { Search, Trash2, CheckCircle, TriangleAlert } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import LogButton from "../common/LogButton";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/SubCategory/SubCategoryTableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

type StatusType = "Active" | "Inactive";

interface SubCategory {
  id: number;
  subcat_name: string;
  parent_category_name: string;
  status: StatusType;
  log: string;
}

const SubCategoryTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof SubCategory | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | null
  >(null);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  const token = sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchSubCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) throw new Error("Unauthorized: Please log in.");
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/subcategory/search/list`,
          { search: "" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === 200) {
          const formattedSubCategories: SubCategory[] = response.data.data
            .map((subcategory: any) => ({
              id: subcategory.id,
              subcat_name: subcategory.subcat_name || "Unnamed",
              parent_category_name: subcategory.parent_category_name || "N/A",
              status: subcategory.status === 1 ? "Active" : "Inactive",
              log: subcategory.log || "",
            }))
            .sort((a: SubCategory, b: SubCategory) => b.id - a.id);
          setSubCategories(formattedSubCategories);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setSubCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
  }, [token]);

  const toggleStatus = async (id: number) => {
    try {
      if (!token) throw new Error("Authentication error! Please log in.");
      const subCategory = subCategories.find((s) => s.id === id);
      if (!subCategory) return;
      const newStatus = subCategory.status === "Active" ? 0 : 1;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subcategory/status/update`,
        { sub_cat_id: id, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setSubCategories((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, status: newStatus === 1 ? "Active" : "Inactive" }
              : s
          )
        );
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to update status."
      );
      setErrorModalOpen(true);
    }
  };

  const handleDeleteClick = (subCategoryId: number) => {
    setSelectedSubCategoryId(subCategoryId);
    openModal();
  };

  const handleConfirmDelete = async () => {
    if (!selectedSubCategoryId || !token) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subcategory/delete`,
        { sub_cat_id: selectedSubCategoryId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setSubCategories((prev) => {
          const updated = prev.filter((s) => s.id !== selectedSubCategoryId);
          if (updated.length > 0 && displayedSubCategories.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updated;
        });
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        closeModal();
      } else {
        throw new Error(
          response.data.message || "Failed to delete subcategory."
        );
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to delete subcategory."
      );
      setErrorModalOpen(true);
    }
  };

  const handleSort = (column: keyof SubCategory) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredSubCategories = subCategories.filter((subCategory) =>
    subCategory.subcat_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedSubCategories = [...filteredSubCategories].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortOrder === "asc"
      ? (valA as number) - (valB as number)
      : (valB as number) - (valA as number);
  });

  const displayedSubCategories = sortedSubCategories.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredSubCategories.length / rowsPerPage);

  const handleDownload = () => {
    const data = searchTerm
      ? filteredSubCategories
      : sortColumn
      ? sortedSubCategories
      : subCategories;

    if (!data.length) return;

    const formattedData = data.map((subCategory) => ({
      "Sub Category Name": subCategory.subcat_name,
      "Parent Category": subCategory.parent_category_name,
      Status: subCategory.status,
      Log: subCategory.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Sub Category List"]], {
      origin: "A1",
    });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C, ...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) => String(row[key as keyof typeof row])?.length || 0
          ),
          "Sub Category List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SubCategories");
    XLSX.writeFile(
      workbook,
      searchTerm ? `SubCategoryData_${searchTerm}.xlsx` : "SubCategoryData.xlsx"
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Sub Category List</h3>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(val) => {
              setRowsPerPage(val);
              setCurrentPage(1);
            }}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
                <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/subcategory-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Subcategory
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.th}
                  onClick={() => handleSort("subcat_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.th}
                  onClick={() => handleSort("parent_category_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Parent Category Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Status</p>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedSubCategories.length ? (
                displayedSubCategories.map((subCategory) => (
                  <tr key={subCategory.id} className={styles.tbodyRow}>
                    <td className={styles.td}>{subCategory.subcat_name}</td>
                    <td className={styles.td}>
                      {subCategory.parent_category_name}
                    </td>
                    <td className={styles.td}>
                      <div className={styles.statusContainer}>
                        <button
                          onClick={() => toggleStatus(subCategory.id)}
                          className={`${styles.statusToggle} ${
                            subCategory.status === "Active"
                              ? styles.statusToggleActive
                              : styles.statusToggleInactive
                          }`}
                        >
                          <div
                            className={`${styles.statusToggleCircle} ${
                              subCategory.status === "Active"
                                ? styles.statusToggleCircleActive
                                : styles.statusToggleCircleInactive
                            }`}
                          ></div>
                        </button>
                        <span
                          className={`${styles.statusBadge} ${
                            subCategory.status === "Active"
                              ? styles.statusActive
                              : styles.statusInactive
                          }`}
                        >
                          {subCategory.status}
                        </span>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                        <Link
                          to={`/subcategory-edit/${subCategory.id}`}
                          className="inline-flex"
                        >
                          <button className={styles.editButton}>
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="text-white text-sm"
                            />
                          </button>
                        </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteClick(subCategory.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-white text-sm"
                          />
                        </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <LogButton log={subCategory.log} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className={styles.noDataText}>
                    No records found...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredSubCategories.length}
          handlePageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <div className={styles.modalIconContainer}>
          <Trash2 className={styles.modalIcon} />
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this subcategory?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Subcategory Status Updated Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Subcategory Deleted Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-red-100 dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-yellow-200 text-yellow-600 rounded-full">
            <TriangleAlert className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Alert!
        </h4>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{errorMessage}</p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setErrorModalOpen(false)}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SubCategoryTable;
